<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import IosCheckbox from 'dashboard/components-next/checkbox/IosCheckbox.vue';
import { dynamicTime } from 'shared/helpers/timeHelper';

const props = defineProps({
  contacts: { type: Array, required: true },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits([
  'sort',
  'view-contact',
  'edit-contact',
  'message-contact',
]);

const { t } = useI18n();

const columns = computed(() => [
  {
    key: 'name',
    label: t('CONTACTS_LAYOUT.FILTER.NAME'),
    icon: 'i-ph-users-three-bold',
    sortable: true,
  },
  {
    key: 'last_activity_at',
    label: t('CONTACTS_LAYOUT.FILTER.LAST_ACTIVITY'),
    icon: 'i-ph-activity-bold',
    sortable: true,
  },
  {
    key: 'created_at',
    label: t('CONTACTS_LAYOUT.FILTER.CREATED_AT'),
    icon: 'i-ph-calendar-bold',
    sortable: true,
  },
  {
    key: 'email',
    label: t('CONTACTS_LAYOUT.FILTER.EMAIL'),
    icon: 'i-ph-envelope-simple-bold',
    sortable: true,
  },
  {
    key: 'phone_number',
    label: t('CONTACTS_LAYOUT.FILTER.PHONE_NUMBER'),
    icon: 'i-ph-phone-bold',
    sortable: false,
  },
  { key: 'vip', label: 'VIP', icon: 'i-ph-crown-bold', sortable: false },
]);

const formatRelativeTime = timestamp => {
  if (!timestamp) return '—';
  return dynamicTime(timestamp);
};

const handleSort = columnKey => {
  const column = columns.value.find(c => c.key === columnKey);
  if (!column?.sortable) return;

  let newOrdering = '';
  if (props.activeSort === columnKey) {
    newOrdering = props.activeOrdering === '' ? '-' : '';
  }

  emit('sort', { sort: columnKey, order: newOrdering });
};

const isSortActive = columnKey => props.activeSort === columnKey;
const getSortIcon = columnKey => {
  if (!isSortActive(columnKey)) return '';
  return props.activeOrdering === '-'
    ? 'i-ph-caret-down-bold'
    : 'i-ph-caret-up-bold';
};
</script>

<template>
  <div class="w-full overflow-x-auto">
    <table class="w-full min-w-[980px] border-collapse">
      <thead>
        <tr class="table-head-row border-b border-n-weak">
          <th class="w-12 px-4 py-3">
            <IosCheckbox :size="18" aria-label="Select all" />
          </th>
          <th
            v-for="col in columns"
            :key="col.key"
            class="px-4 py-3 text-left text-xs font-semibold text-n-slate-11 select-none whitespace-nowrap"
            :class="col.sortable ? 'cursor-pointer hover:text-n-slate-12' : ''"
            @click="handleSort(col.key)"
          >
            <span class="inline-flex items-center gap-2">
              <span class="size-4 text-n-slate-11" :class="[col.icon]" />
              <span>{{ col.label }}</span>
              <span
                v-if="isSortActive(col.key)"
                class="size-3.5 text-n-slate-11"
                :class="[getSortIcon(col.key)]"
              />
            </span>
          </th>
          <th
            class="w-24 px-4 py-3 text-right text-xs font-semibold text-n-slate-11"
          >
            {{ t('CONTACT_PANEL.CONTACT_ACTIONS') }}
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="contact in contacts"
          :key="contact.id"
          class="border-b border-n-weak hover:bg-n-alpha-2 cursor-pointer"
          @click="emit('view-contact', contact.id)"
        >
          <td class="w-12 px-4 py-3" @click.stop>
            <IosCheckbox :size="18" aria-label="Select row" />
          </td>

          <!-- Name -->
          <td class="px-4 py-3">
            <div class="flex items-center gap-3 min-w-0">
              <Avatar
                :name="contact.name"
                :src="contact.thumbnail"
                :size="32"
                :status="contact.availabilityStatus"
                hide-offline-status
                rounded-full
              />
              <div class="min-w-0">
                <div class="text-sm font-medium text-n-slate-12 truncate">
                  {{ contact.name || '—' }}
                </div>
                <div
                  v-if="contact.additionalAttributes?.companyName"
                  class="text-xs text-n-slate-11 truncate"
                >
                  {{ contact.additionalAttributes.companyName }}
                </div>
              </div>
            </div>
          </td>

          <!-- Last activity -->
          <td class="px-4 py-3 text-sm text-n-slate-11 whitespace-nowrap">
            {{ formatRelativeTime(contact.lastActivityAt) }}
          </td>

          <!-- Created at -->
          <td class="px-4 py-3 text-sm text-n-slate-11 whitespace-nowrap">
            {{ formatRelativeTime(contact.createdAt) }}
          </td>

          <!-- Email -->
          <td class="px-4 py-3 text-sm text-n-slate-11 truncate max-w-[14rem]">
            {{ contact.email || '—' }}
          </td>

          <!-- Phone -->
          <td class="px-4 py-3 text-sm text-n-slate-11 whitespace-nowrap">
            {{ contact.phoneNumber || '—' }}
          </td>

          <!-- VIP -->
          <td class="px-4 py-3 text-sm text-n-slate-11 whitespace-nowrap">
            <span
              v-if="contact.customAttributes?.vip"
              class="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-n-amber-9/10 text-n-amber-11"
            >
              <span class="i-ph-crown-bold size-3.5" />
              {{ contact.customAttributes.vip }}
            </span>
            <span v-else>—</span>
          </td>

          <!-- Actions -->
          <td class="px-4 py-3" @click.stop>
            <div class="flex items-center justify-end gap-1">
              <Button
                icon="i-ph-chat-circle-dots-bold"
                variant="ghost"
                color="slate"
                size="sm"
                :aria-label="t('CONTACTS_LAYOUT.TABLE.SEND_MESSAGE')"
                @click="emit('message-contact', contact.id)"
              />
              <Button
                icon="i-ph-pencil-simple-bold"
                variant="ghost"
                color="slate"
                size="sm"
                :aria-label="t('CONTACTS_LAYOUT.TABLE.EDIT')"
                @click="emit('edit-contact', contact)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      v-if="contacts.length === 0"
      class="flex flex-col items-center justify-center py-16 text-n-slate-11"
    >
      <span class="i-ph-users-three-bold size-10" />
      <p class="mt-3 text-sm">
        {{ t('CONTACTS_LAYOUT.EMPTY_STATE.LIST_EMPTY_STATE_TITLE') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.table-head-row {
  background: #f8fafc;
}
</style>
